import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useStartImpersonation, useUsers } from '../api/hooks.js';

// Sélecteur de « Voir comme ». Réutilise la liste des utilisateurs
// des Paramètres → Accès. Les comptes BOSS et le sien propre sont
// hors liste : le serveur les refuse de toute façon.

interface Props {
  open: boolean;
  currentUserId: string;
  onClose: () => void;
}

export function ImpersonateModal({ open, currentUserId, onClose }: Props) {
  const users = useUsers(open);
  const start = useStartImpersonation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users.data ?? [])
      .filter((u) => u.active && u.role !== 'BOSS' && u.id !== currentUserId)
      .filter((u) => !needle || u.name.toLowerCase().includes(needle) || u.email.includes(needle));
  }, [users.data, q, currentUserId]);

  if (!open) return null;

  const pick = async (userId: string) => {
    await start.mutateAsync(userId);
    onClose();
    navigate('/', { replace: true });
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="impersonate-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="impersonate-modal"
      >
        <h2 id="impersonate-title">Voir comme…</h2>
        <p>
          L'application s'affichera avec les droits de la personne choisie, en
          lecture seule. Aucune écriture n'est possible tant que vous n'êtes pas
          sorti.
        </p>
        <input
          type="search"
          className="imp-search"
          placeholder="Rechercher un utilisateur"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Rechercher un utilisateur"
        />
        <div className="imp-list">
          {users.isLoading && <p className="muted">Chargement…</p>}
          {!users.isLoading && rows.length === 0 && (
            <p className="muted">Aucun utilisateur correspondant.</p>
          )}
          {rows.map((u) => (
            <button
              key={u.id}
              type="button"
              className="imp-list-item"
              onClick={() => pick(u.id)}
              disabled={start.isPending}
            >
              <span className="imp-list-name">{u.name}</span>
              <span className="imp-list-meta">{u.role} · {u.email}</span>
            </button>
          ))}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
